import React, { useState, memo, useContext, useRef, useMemo, useEffect } from "react"
import { Card, CardContent, CardHeader } from "@components/common/card"
import { Button } from "@components/common/button"
import useTranslator from "@pma/hooks/use-translator"
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react"
import { DictType } from "@pma/types"
import DeleteTask from "./delete-task"
import TaskStatusEditor from "./task-status-editor"
import AssignToUser from "./assign-to-user"
import ExpandTask from "./expand-task"
import { formatStringWithLink } from "@pma/utils/helpers"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import PinTask from "./pin-task"
import TaskPriorityEditor from "./task-priority-editor"
import SubTaskCard from "./task-mobile-view/Sub-task-card"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../common/accordion"
import AddSubTaskInput from "./task-mobile-view/Add-sub-card"
import CardEditor from "./task-mobile-view/card-editor"
import { useSortable, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"

const SubTaskList: React.FC<{ subtasks: any[]; parentTaskId: number; group_id?: number | null }> =
  memo(
    ({ subtasks, parentTaskId, group_id }) => {
      const subtaskIds = React.useMemo(
        () => subtasks.map((subtask) => `subtask_${parentTaskId}_${subtask.id}_${group_id ?? -1}`),
        [subtasks, parentTaskId, group_id],
      )

      return (
        <SortableContext items={subtaskIds} strategy={verticalListSortingStrategy}>
          <div className='flex flex-col gap-2'>
            {subtasks.map((val: any, index: number) => (
              <SubTaskCard
                task={val}
                key={`subtask_${parentTaskId}_${val.id}`}
                parentTaskId={parentTaskId}
                index={index}
                group_id={group_id}
              />
            ))}
          </div>
        </SortableContext>
      )
    },
    (prevProps, nextProps) => {
      return (
        prevProps.parentTaskId === nextProps.parentTaskId &&
        prevProps.group_id === nextProps.group_id &&
        prevProps.subtasks === nextProps.subtasks
      )
    },
  )

interface Props {
  task: DictType
  index: number
  group_id?: number | null
  onRef: any
}

const colors = ["#A52A2A", "#F11010", "#F17010", "#F1D310", "#259F0A", "#147CE1", "#9914E1"]

function TaskCardView({ task, index, group_id, onRef }: Props) {
  const { translate } = useTranslator()
  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setExpanded] = useState(false)
  const [accordionValue, setAccordionValue] = useState<string>("")
  const { search, setTaskExpandForLoading } = useContext(GroupedTasksContext)
  const hasRunSearchRef = useRef(false)
  const sortableId = `task_${task.id}_${group_id ?? -1}`

  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: sortableId,
    data: {
      type: "Task",
      task,
      isSubtask: false,
      groupId: group_id ?? -1,
    },
    disabled: !task.id,
  })

  const style = {
    transition: isDragging ? "transform 0.1s ease" : transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : "auto",
    willChange: "transform",
  }

  const handleSubShow = () => {
    if (!isOpen) {
      setTaskExpandForLoading(task.id, { toggleExpanded: () => {} } as any)
    }
    setIsOpen(!isOpen)
  }

  const hasMatchingSubtask = useMemo(() => {
    if (!search || !task._subtasks || task._subtasks.length === 0) return false
    const searchLower = search.toLowerCase()
    return task._subtasks.some((subtask: any) => {
      const taskIdFormatted = `PR-${subtask.id}`.toLowerCase()
      return (
        taskIdFormatted.includes(searchLower) ||
        subtask.title?.toLowerCase().includes(searchLower) ||
        subtask.description?.toLowerCase().includes(searchLower)
      )
    })
  }, [search, task._subtasks])

  useEffect(() => {
    if (search && !hasRunSearchRef.current && hasMatchingSubtask && task._subtasks?.length > 0) {
      if (!isExpanded) {
        setExpanded(true)
      }
      if (!accordionValue.includes("sub-task")) {
        setAccordionValue("sub-task")
      }
      hasRunSearchRef.current = true
    }
    // Reset ref when search changes
    if (!search) {
      hasRunSearchRef.current = false
    }
  }, [search, hasMatchingSubtask, isExpanded, task._subtasks, accordionValue])

  const getColor = (index: number) => {
    const mod = index % colors.length
    return colors[mod]
  }
  const color = getColor(index + 1)

  const internalRef = (node: HTMLDivElement) => {
    setNodeRef(node)
    if (onRef) {
      onRef(task.id, node)
    }
  }

  return (
    <div ref={internalRef} style={style} {...attributes}>
      <Card className='sortable-card h-fit hover:ring-2 hover:ring-inset hover:ring-primary/40'>
        <CardHeader className='flex flex-row items-center justify-between px-5 py-3'>
          <div className='flex w-full flex-row gap-5 sm:w-[200px] md:w-[230px]'>
            <div>
              <p className='text-xs font-semibold'>{translate(`Ticket`)}</p>
              <p className='text-xs text-slate-500'>{`${translate("PR")}-${task.id}`}</p>
            </div>
            <div>
              <p className='text-xs font-semibold'>{translate("Title")}</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className='w-[18ch] truncate text-ellipsis text-xs text-slate-500 sm:w-[8ch] md:w-[22ch]'>
                    {task?.title}
                  </p>
                </TooltipTrigger>
                <TooltipContent side='bottom' className='flex items-center gap-4'>
                  <p className='truncate text-ellipsis text-xs text-slate-500'>{task?.title}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          <div className='flex items-center gap-2'>
            {task.id && (
              <div
                {...listeners}
                className='cursor-grab touch-none select-none p-2'
                style={{ touchAction: "none" }}
              >
                <GripVertical className='text-gray-500' size={20} />
              </div>
            )}
            <Button
              size='sm'
              className='m-0 h-8 w-8 rounded-full p-0'
              onClick={() => setExpanded(!isExpanded)}
            >
              {isExpanded ? <ChevronUp /> : <ChevronDown />}
            </Button>
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className='flex flex-col gap-3 p-2 text-sm'>
            <div className='w-full'>
              <div className='flex flex-col gap-3 overflow-hidden p-2'>
                <div className='flex flex-row gap-2'>
                  <span className='flex items-center text-xs text-slate-500'>
                    {translate("Title")}:
                  </span>
                  <CardEditor id={task?.id} title='title' value={task?.title}>
                    <div
                      className='max-w-[30ch] truncate text-ellipsis text-left text-sm md:w-full'
                      dangerouslySetInnerHTML={{
                        __html: formatStringWithLink(task.title),
                      }}
                    />
                  </CardEditor>
                </div>
                <div className='flex flex-row gap-2'>
                  <span className='flex items-center text-xs text-slate-500'>
                    {translate("Description")}:
                  </span>
                  <CardEditor id={task?.id} title='description' value={task?.description}>
                    {task?.description ? (
                      <div
                        className='max-w-[28ch] truncate text-ellipsis text-left text-xs text-slate-500 md:w-full'
                        dangerouslySetInnerHTML={{
                          __html: formatStringWithLink(task?.description),
                        }}
                      />
                    ) : (
                      "---"
                    )}
                  </CardEditor>
                </div>
                <div className='flex w-full items-center justify-between gap-3'>
                  <div className='flex flex-1 items-center gap-2'>
                    <span className='truncate text-ellipsis text-xs text-slate-500'>
                      {translate("Status")}:
                    </span>
                    <TaskStatusEditor status={task?.status} taskId={task?.id} />
                  </div>
                </div>
                <div className='flex w-full items-center justify-between gap-3'>
                  <div className='flex flex-1 items-center gap-2'>
                    <span className='truncate text-ellipsis text-xs text-slate-500'>
                      {translate("Priority")}:
                    </span>
                    <TaskPriorityEditor priority={task?._priority} taskId={task?.id} />
                  </div>
                </div>
                <div className='flex w-full items-center justify-between'>
                  {task?.responsible_id && (
                    <div className='flex flex-1 gap-2'>
                      <span className='truncate text-ellipsis text-xs text-slate-500'>
                        {translate("Assign users")}:
                      </span>
                      <AssignToUser taskId={task?.id} responsible={task?.responsible_id || []} />
                    </div>
                  )}
                </div>
                <div className='flex w-full items-center justify-between gap-0.5'>
                  <small className='text-xs text-slate-500'>{`${translate("PR")}-${task.id}`}</small>
                  <div className='flex flex-1 items-center justify-end'>
                    <PinTask task={task} />
                    <ExpandTask task={task} />
                    <DeleteTask task={task} />
                  </div>
                </div>
              </div>
              <Accordion
                type='single'
                collapsible
                className='flex flex-col gap-3 pt-3'
                value={accordionValue}
                onValueChange={setAccordionValue}
              >
                <AccordionItem
                  value='sub-task'
                  className='rounded-xl border border-l-8 px-2 py-2'
                  style={{ borderLeftColor: color }}
                >
                  <AccordionTrigger className='py-1 hover:no-underline' onClick={handleSubShow}>
                    {translate("Sub-tasks")} {task?._subtasks?.length ?? 0}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className='flex flex-col gap-2'>
                      {task?._subtasks?.length > 0 ? (
                        <SubTaskList
                          subtasks={task._subtasks}
                          parentTaskId={task.id}
                          group_id={group_id}
                        />
                      ) : (
                        <p className='text-xs text-slate-500'>{translate("No subtasks")}</p>
                      )}
                      <AddSubTaskInput task={task} />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  )
}

export default TaskCardView
